import { useCallback } from "react";

import { useTopicsDataContext } from "app/pages/activation/context/Topics/TopicsDataContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import type { TopicWithKeywordsSchema } from "app/pages/activation/components/Topics/types";
import { CreationFlow } from "app/pages/activation/components/Topics/types";
import type { CreatedTopicDTO } from "app/pages/activation/queries/topics/useCreateTopicsMutation";
import { useCreateTopicsMutation } from "app/pages/activation/queries/topics/useCreateTopicsMutation";

import type { BenchmarkGranularity } from "../../../types/common";

import { useDuplicateGate } from "./useDuplicateGate";

type Track = {
    trackTopicCreate: (namesStr: string, count: number, from: CreationFlow) => void;
    trackLinkKWListCreate: (namesStr: string, count: number, from: CreationFlow) => void;
    trackTopicCancelCreation: (from?: CreationFlow) => void;
};

type UseCreateFlowsSchema = {
    activationId: string;
    track: Track;
    granularity: BenchmarkGranularity;
    handleSuccessCreation: (
        created: string | CreatedTopicDTO[],
        from: CreationFlow,
    ) => Promise<{ createdTopicsStr: string; from: CreationFlow }>;
};

export const useCreateFlows = ({ activationId, granularity, track, handleSuccessCreation }: UseCreateFlowsSchema) => {
    const { mutateAsync: createTopics, isPending: isLoading } = useCreateTopicsMutation(activationId, granularity);

    const { keywordsGroups } = useTopicsDataContext();
    const { onCollision, onClose } = useTopicsModalContext();
    const { duplicateTopics, splitUniquesAndDups } = useDuplicateGate(keywordsGroups);

    const splitByNames = useCallback(
        (names: string[]) => splitUniquesAndDups(names),
        [splitUniquesAndDups, keywordsGroups],
    );

    const createByNames = useCallback(
        async (names: string[], from: CreationFlow) => {
            if (!names?.length) {
                return;
            }

            const topicsNumber = names.length;

            const { uniques, dups } = splitByNames(names);

            if (dups.length > 0) {
                onCollision(dups, topicsNumber, (n: string[]) => createByNames(n, from));
            }

            if (!uniques.length) {
                return;
            }

            await createTopics({
                activationId,
                body: uniques,
                __onSuccess: async (created) => {
                    if (dups.length === 0) {
                        const { createdTopicsStr } = await handleSuccessCreation(created, from);
                        track.trackTopicCreate(createdTopicsStr, created.length, from);
                    }
                },
            });
        },
        [activationId, createTopics, handleSuccessCreation, splitByNames, track, onCollision],
    );

    const onCreateWithKeywords = useCallback(
        async (bodies: TopicWithKeywordsSchema[]) => {
            if (!bodies?.length) {
                return;
            }

            await createTopics({
                activationId,
                body: bodies,
                isWithKeywords: true,
                __onSuccess: async (created) => {
                    const { createdTopicsStr } = await handleSuccessCreation(created, CreationFlow.Manual);
                    track.trackTopicCreate(createdTopicsStr, created.length, CreationFlow.Manual);
                },
            });
        },
        [activationId, createTopics, handleSuccessCreation, track],
    );

    const onCreateFromKW = useCallback(
        async (ids: string[]) => {
            if (!ids?.length) {
                return;
            }

            await createTopics({
                activationId,
                body: ids,
                isWithKeywords: false,
                isFromExistingKW: true,
                __onSuccess: async (created) => {
                    const { createdTopicsStr, from } = await handleSuccessCreation(
                        created,
                        CreationFlow.ImportFromExisting,
                    );
                    track.trackLinkKWListCreate(createdTopicsStr, created.length, from);
                },
            });
        },
        [activationId, createTopics, handleSuccessCreation, track],
    );

    const onCreate = useCallback(
        async (names: string[]) => {
            await createByNames(names, CreationFlow.Suggestion);
        },
        [createByNames],
    );

    const onCancel = useCallback(
        (from?: CreationFlow) => () => {
            track.trackTopicCancelCreation(from);
            onClose();
        },
        [track],
    );

    return { isLoading, duplicateTopics, onCreateFromKW, onCreateWithKeywords, onCreate, onCancel };
};
